import cn from 'classnames'
import React, { useEffect, useRef } from 'react'
import type { ContentWriterInformation } from 'src/hooks/useContentWriterInformation'

import WriterTooltipModel from './WriterTooltipModel'

const AUTHOR_MORE_TRIGGER_CLASS =
  'relative z-[9999] inline-block cursor-pointer rounded px-1 pb-[3px] pt-[3px] text-[12px] font-medium text-blue-600'

interface InsightCardAuthorMoreProps {
  insightCardId: string
  secondWriter: ContentWriterInformation
  openTooltipCardId?: string | null
  setOpenTooltipCardId: (id: string | null) => void
}

export const InsightCardAuthorMore: React.FC<InsightCardAuthorMoreProps> = ({
  insightCardId,
  secondWriter,
  openTooltipCardId,
  setOpenTooltipCardId,
}) => {
  const authorTooltipCardId = `${insightCardId}-writers`
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const isTooltipOpen = openTooltipCardId === authorTooltipCardId

  const handleTooltipToggle = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (isTooltipOpen) {
      setOpenTooltipCardId(null)
    } else {
      setOpenTooltipCardId(authorTooltipCardId)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isTooltipOpen) return

      const target = event.target as Node
      if (triggerRef.current?.contains(target) || tooltipRef.current?.contains(target)) {
        return
      }

      setOpenTooltipCardId(null)
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isTooltipOpen, setOpenTooltipCardId])

  return (
    <span className="relative inline-block">
      {isTooltipOpen && (
        <div ref={tooltipRef}>
          <WriterTooltipModel writer={secondWriter} />
        </div>
      )}
      <span
        id={authorTooltipCardId}
        ref={triggerRef}
        role="button"
        tabIndex={0}
        className={cn(AUTHOR_MORE_TRIGGER_CLASS, isTooltipOpen && 'tooltip-open')}
        onClick={handleTooltipToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleTooltipToggle(event as unknown as React.MouseEvent<HTMLSpanElement>)
          }
        }}
        aria-label={`Also authored by ${secondWriter.name}`}
        aria-expanded={isTooltipOpen}
      >
        {isTooltipOpen ? '- less' : '+1 more'}
      </span>
    </span>
  )
}
